import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  IconButton,
  useTheme,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

// Icons
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ClearIcon from '@mui/icons-material/Clear';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import PieChartIcon from '@mui/icons-material/PieChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';

// Components
import ChartContainer from '../components/common/ChartContainer';
import EnhancedChart from '../components/common/EnhancedChart';
import { dataTypeColors } from '../utils/colorUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

// Helper function to safely format numbers
const formatNumber = (value) => {
  return value !== undefined && value !== null ? value.toLocaleString() : '0';
};

const ComparisonAnalytics = () => {
  const theme = useTheme();
  // Add chart refs for charts
  const mainChartRef = useRef(null);
  const tableChartRef = useRef(null);
  
  const [filters, setFilters] = useState({
    year: new Date().getFullYear().toString(),
    month: ''
  });
  
  const [allMakers, setAllMakers] = useState([]);
  const [selectedMakers, setSelectedMakers] = useState([]);
  const [compareData, setCompareData] = useState([]);
  const [comparisonType, setComparisonType] = useState('combined'); // 'ev', 'ice', or 'combined'
  const [viewType, setViewType] = useState('bar');
  const [stackedView, setStackedView] = useState(true);
  
  const [isLoading, setIsLoading] = useState({
    makersDropdown: true,
    comparisonData: false
  });

  // Get current year and available years for dropdown
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
  
  // Months for dropdown
  const months = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Feb' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' },
    { value: '05', label: 'May' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Aug' },
    { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
    { value: '', label: 'All' }
  ];

  // Fetch all makers for dropdown
  useEffect(() => {
    const fetchMakers = async () => {
      try {
        setIsLoading(prev => ({ ...prev, makersDropdown: true }));
        const response = await axios.get('/api/makers');
        setAllMakers(response.data);
      } catch (error) {
        console.error('Error fetching makers:', error);
      } finally {
        setIsLoading(prev => ({ ...prev, makersDropdown: false }));
      }
    };
    
    fetchMakers();
  }, []);

  // Fetch comparison data when filters or selected makers change
  useEffect(() => {
    const fetchComparisonData = async () => {
      if (selectedMakers.length === 0) {
        setCompareData([]);
        return;
      }
      
      try {
        setIsLoading(prev => ({ ...prev, comparisonData: true }));
        
        let endpoint = '/api/sales/compare-makers';
        let params = {
          year: filters.year,
          month: filters.month,
          makers: selectedMakers.join(',')
        };
        
        // Choose the endpoint based on comparison type
        if (comparisonType === 'combined') {
          endpoint = '/api/sales/compare-makers-combined';
        } else if (comparisonType === 'ev') {
          params.type = 'ev';
        } else if (comparisonType === 'ice') {
          params.type = 'ice';
        }
        
        const response = await axios.get(endpoint, { params });
        setCompareData(response.data);
      } catch (error) {
        console.error('Error fetching comparison data:', error);
      } finally {
        setIsLoading(prev => ({ ...prev, comparisonData: false }));
      }
    };
    
    fetchComparisonData();
  }, [selectedMakers, filters.year, filters.month, comparisonType]);
  
  const handleAddMaker = (maker) => {
    if (maker && !selectedMakers.includes(maker)) {
      setSelectedMakers([...selectedMakers, maker]);
    }
  };
  
  const handleRemoveMaker = (maker) => {
    setSelectedMakers(selectedMakers.filter(m => m !== maker));
  };
  
  const handleViewTypeChange = (type) => {
    setViewType(type);
  };
  
  const handleComparisonTypeChange = (event, newType) => {
    if (newType !== null) {
      setComparisonType(newType);
    }
  };
  
  const handleStackedViewChange = () => {
    setStackedView(!stackedView);
  };
  
  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value
    });
  };

  // Prepare chart data based on comparison type
  const getChartData = () => {
    if (!compareData || compareData.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    // If a specific month is selected, filter the monthly data to only show that month
    const filteredData = filters.month 
      ? compareData.map(maker => ({
          ...maker,
          monthlyData: maker.monthlyData?.filter(item => {
            // Check if the month matches the selected month (e.g., "Jan" matches "01")
            const monthIndex = months.findIndex(m => m.value === filters.month);
            return monthIndex >= 0 && item.month === months[monthIndex].label;
          })
        }))
      : compareData;
    
    // Get months from the first maker's data
    const months = filteredData[0]?.monthlyData
      ? filteredData[0].monthlyData.map(item => item.month)
      : [];
    
    if (comparisonType === 'combined') {
      if (stackedView) {
        // Stacked view showing both EV and ICE for each maker
        const datasets = [];
        
        // Add ICE datasets
        filteredData.forEach((maker, index) => {
          const hue = (index * 137) % 360;
          datasets.push({
            label: `${maker.maker} (ICE)`,
            data: maker.monthlyData ? maker.monthlyData.map(item => item.ice) : [],
            backgroundColor: `hsla(${hue}, 70%, 60%, 0.6)`,
            borderColor: `hsla(${hue}, 70%, 50%, 1)`,
            stack: maker.maker,
          });
          
          // Add EV datasets
          datasets.push({
            label: `${maker.maker} (EV)`,
            data: maker.monthlyData ? maker.monthlyData.map(item => item.ev) : [],
            backgroundColor: `hsla(${hue}, 70%, 60%, 0.9)`,
            borderColor: `hsla(${hue}, 70%, 50%, 1)`,
            stack: maker.maker,
          });
        });
        
        return {
          labels: months,
          datasets
        };
      } else {
        // Grouped view comparing totals
        const datasets = [
          {
            label: 'Total EV Sales',
            data: filteredData.map(maker => maker.totalSales?.ev),
            backgroundColor: dataTypeColors.ev.primary,
            borderColor: dataTypeColors.ev.primary,
          },
          {
            label: 'Total ICE Sales',
            data: filteredData.map(maker => maker.totalSales?.ice),
            backgroundColor: dataTypeColors.ice.primary,
            borderColor: dataTypeColors.ice.primary,
          },
          {
            label: 'Total Sales',
            data: filteredData.map(maker => {
              if (maker.totalSales?.ev !== undefined && maker.totalSales?.ice !== undefined) {
                return maker.totalSales.ev + maker.totalSales.ice;
              }
              return 0;
            }),
            backgroundColor: dataTypeColors.combined.primary,
            borderColor: dataTypeColors.combined.primary,
          }
        ];
        
        return {
          labels: filteredData.map(maker => maker.maker),
          datasets
        };
      }
    } else {
      // Single type comparison (EV or ICE)
      const datasets = filteredData.map((maker, index) => {
        const hue = (index * 137) % 360;
        return {
          label: maker.maker,
          data: maker.monthlyData ? maker.monthlyData.map(item => item.sales) : [],
          backgroundColor: `hsla(${hue}, 70%, 60%, 0.6)`,
          borderColor: `hsla(${hue}, 70%, 50%, 1)`,
          tension: 0.1
        };
      });
      
      return {
        labels: months,
        datasets
      };
    }
  };
  
  // Prepare pie chart data for combined comparisons
  const getPieChartData = () => {
    if (!compareData || compareData.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    return {
      labels: compareData.map(maker => maker.maker),
      datasets: [
        {
          label: 'Total EV Sales',
          data: compareData.map(maker => maker.totalSales?.ev),
          backgroundColor: compareData.map((_, index) => {
            const hue = (index * 137) % 360;
            return `hsla(${hue}, 70%, 60%, 0.8)`;
          }),
          borderColor: compareData.map((_, index) => {
            const hue = (index * 137) % 360;
            return `hsla(${hue}, 70%, 50%, 1)`;
          }),
        },
        {
          label: 'Total ICE Sales',
          data: compareData.map(maker => maker.totalSales?.ice),
          backgroundColor: compareData.map((_, index) => {
            const hue = (index * 137) % 360;
            return `hsla(${hue}, 70%, 60%, 0.5)`;
          }),
          borderColor: compareData.map((_, index) => {
            const hue = (index * 137) % 360;
            return `hsla(${hue}, 70%, 50%, 1)`;
          }),
        }
      ]
    };
  };
  
  const chartData = getChartData();
  const pieChartData = getPieChartData();

  // Generate a title for the main chart based on the current view
  const getMainChartTitle = () => {
    let typeText = comparisonType === 'combined' 
      ? 'Combined ICE and EV' 
      : comparisonType === 'ev' 
        ? 'EV' 
        : 'ICE';
    
    let viewText = viewType === 'bar' 
      ? 'Bar Chart' 
      : viewType === 'line' 
        ? 'Line Chart' 
        : 'Pie Chart';
    
    return `${typeText} Sales Comparison - ${viewText}`;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 0,
      p: 1
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1
      }}>
        <Typography variant="h5" noWrap>Comparison Analytics</Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 70, width: 'auto' }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              name="year"
              value={filters.year}
              label="Year"
              onChange={handleFilterChange}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 70, width: 'auto' }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              name="month"
              value={filters.month}
              label="Month"
              onChange={handleFilterChange}
            >
              {months.map(month => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      <Card elevation={1} sx={{ mb: 1 }}>
        <CardContent sx={{ p: '8px !important', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, flexWrap: 'wrap', gap: 1 }}>
            <Autocomplete
              options={allMakers || []}
              loading={isLoading.makersDropdown}
              value={null}
              onChange={(event, newValue) => handleAddMaker(newValue)}
              sx={{ width: 150 }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add maker"
                  size="small"
                />
              )}
              disabled={selectedMakers.length >= 5}
            />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedMakers.map(maker => (
                <Chip 
                  key={maker}
                  label={maker}
                  onDelete={() => handleRemoveMaker(maker)}
                  deleteIcon={<ClearIcon fontSize="small" />}
                  size="small"
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                    height: 24
                  }}
                />
              ))}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={(e, newValue) => newValue && handleViewTypeChange(newValue)}
              size="small"
            >
              <ToggleButton value="bar" sx={{ p: 0.5 }}>
                <Tooltip title="Bar Chart">
                  <BarChartIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="line" sx={{ p: 0.5 }}>
                <Tooltip title="Line Chart">
                  <TimelineIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="pie" disabled={comparisonType !== 'combined'} sx={{ p: 0.5 }}>
                <Tooltip title="Pie Chart">
                  <PieChartIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            
            <ToggleButtonGroup
              value={comparisonType}
              exclusive
              onChange={handleComparisonTypeChange}
              size="small"
            >
              <ToggleButton value="ev" sx={{ 
                p: 0.5,
                color: comparisonType === 'ev' ? 'white' : dataTypeColors.ev.primary,
                backgroundColor: comparisonType === 'ev' ? dataTypeColors.ev.primary : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: dataTypeColors.ev.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: alpha(dataTypeColors.ev.primary, 0.8),
                  }
                },
                '&:hover': {
                  backgroundColor: alpha(dataTypeColors.ev.primary, 0.1),
                }
              }}>
                EV
              </ToggleButton>
              <ToggleButton value="ice" sx={{ 
                p: 0.5,
                color: comparisonType === 'ice' ? 'white' : dataTypeColors.ice.primary,
                backgroundColor: comparisonType === 'ice' ? dataTypeColors.ice.primary : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: dataTypeColors.ice.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: alpha(dataTypeColors.ice.primary, 0.8),
                  }
                },
                '&:hover': {
                  backgroundColor: alpha(dataTypeColors.ice.primary, 0.1),
                }
              }}>
                ICE
              </ToggleButton>
              <ToggleButton value="combined" sx={{ 
                p: 0.5,
                color: comparisonType === 'combined' ? 'white' : dataTypeColors.combined.primary,
                backgroundColor: comparisonType === 'combined' ? dataTypeColors.combined.primary : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: dataTypeColors.combined.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: alpha(dataTypeColors.combined.primary, 0.8),
                  }
                },
                '&:hover': {
                  backgroundColor: alpha(dataTypeColors.combined.primary, 0.1),
                }
              }}>
                Both
              </ToggleButton>
            </ToggleButtonGroup>
            
            {comparisonType === 'combined' && viewType !== 'pie' && (
              <Tooltip title={stackedView ? "Switch to grouped view" : "Switch to stacked view"}>
                <IconButton 
                  onClick={handleStackedViewChange}
                  color={stackedView ? "primary" : "default"}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <StackedBarChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
      
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedMakers.length === 0 ? (
          <Box 
            sx={{ 
              flexGrow: 1,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              boxShadow: 1
            }}
          >
            <CompareArrowsIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              Add makers to compare
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare up to 5 makers across EV and ICE platforms
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%', 
            gap: 1, 
            overflow: 'hidden'
          }}>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <ChartContainer 
                title={getMainChartTitle()}
                isLoading={isLoading.comparisonData}
                sx={{ height: '100%' }}
                titleVariant="subtitle1"
                chartRef={!isLoading.comparisonData ? mainChartRef : null}
                chartId={`comparison-${comparisonType}-${viewType}`}
                chartType={viewType}
              >
                <Box sx={{ height: '100%', width: '100%' }}>
                  {viewType === 'bar' && (
                    <EnhancedChart
                      ref={mainChartRef}
                      type="bar"
                      data={chartData}
                      options={{
                        scales: {
                          y: {
                            beginAtZero: true,
                            stacked: comparisonType === 'combined' && stackedView
                          },
                          x: {
                            stacked: comparisonType === 'combined' && stackedView
                          }
                        }
                      }}
                      colorPalette={comparisonType === 'ev' ? 'ev' : comparisonType === 'ice' ? 'ice' : 'combined'}
                    />
                  )}
                  {viewType === 'line' && (
                    <EnhancedChart
                      ref={mainChartRef}
                      type="line"
                      data={chartData}
                      options={{
                        scales: {
                          y: {
                            beginAtZero: true
                          }
                        }
                      }}
                      colorPalette={comparisonType === 'ev' ? 'ev' : comparisonType === 'ice' ? 'ice' : 'combined'}
                    />
                  )}
                  {viewType === 'pie' && comparisonType === 'combined' && (
                    <EnhancedChart
                      ref={mainChartRef}
                      type="pie"
                      data={pieChartData}
                      colorPalette="combined"
                    />
                  )}
                </Box>
              </ChartContainer>
            </Box>
            
            <Box sx={{ height: 180, minHeight: 180 }}>
              <ChartContainer 
                title="Sales Data Comparison"
                isLoading={isLoading.comparisonData}
                sx={{ height: '100%' }}
                titleVariant="subtitle1"
                chartRef={null} // Tables can't use the chart actions
              >
                <TableContainer component={Paper} sx={{ height: 140, overflow: 'auto' }}>
                  <Table stickyHeader size="small" aria-label="comparison table">
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '0.75rem', 
                            p: '6px 8px',
                            backgroundColor: theme.palette.background.paper
                          }}
                        >
                          Maker
                        </TableCell>
                        {comparisonType === 'combined' && (
                          <>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '0.75rem', 
                                p: '6px 8px',
                                backgroundColor: theme.palette.background.paper
                              }} 
                              align="right"
                            >
                              Total EV Sales
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '0.75rem', 
                                p: '6px 8px',
                                backgroundColor: theme.palette.background.paper
                              }} 
                              align="right"
                            >
                              Total ICE Sales
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '0.75rem', 
                                p: '6px 8px',
                                backgroundColor: theme.palette.background.paper
                              }} 
                              align="right"
                            >
                              Total Sales
                            </TableCell>
                          </>
                        )}
                        {comparisonType !== 'combined' && (
                          <TableCell 
                            sx={{ 
                              fontWeight: 'bold', 
                              fontSize: '0.75rem', 
                              p: '6px 8px',
                              backgroundColor: theme.palette.background.paper
                            }} 
                            align="right"
                          >
                            Total Sales
                          </TableCell>
                        )}
                        {compareData.length > 0 && compareData[0].monthlyData && (
                          compareData[0].monthlyData.map(item => (
                            <TableCell 
                              key={item.month} 
                              sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '0.75rem', 
                                p: '6px 8px',
                                backgroundColor: theme.palette.background.paper
                              }} 
                              align="right"
                            >
                              {item.month}
                            </TableCell>
                          ))
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compareData.map((maker) => (
                        <TableRow key={maker.maker} hover>
                          <TableCell 
                            component="th" 
                            scope="row"
                            sx={{ fontSize: '0.75rem', p: '6px 8px' }}
                          >
                            {maker.maker}
                          </TableCell>
                          {comparisonType === 'combined' && (
                            <>
                              <TableCell align="right" sx={{ fontSize: '0.75rem', p: '6px 8px' }}>
                                {formatNumber(maker.totalSales?.ev)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem', p: '6px 8px' }}>
                                {formatNumber(maker.totalSales?.ice)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem', p: '6px 8px' }}>
                                {formatNumber(
                                  maker.totalSales?.ev !== undefined && maker.totalSales?.ice !== undefined 
                                    ? maker.totalSales.ev + maker.totalSales.ice 
                                    : undefined
                                )}
                              </TableCell>
                            </>
                          )}
                          {comparisonType !== 'combined' && (
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: '6px 8px' }}>
                              {formatNumber(maker.totalSales)}
                            </TableCell>
                          )}
                          {maker.monthlyData && maker.monthlyData.map(item => (
                            <TableCell 
                              key={`${maker.maker}-${item.month}`} 
                              align="right"
                              sx={{ fontSize: '0.75rem', p: '6px 8px' }}
                            >
                              {comparisonType === 'combined' 
                                ? `${formatNumber(item.ev)}/${formatNumber(item.ice)}`
                                : formatNumber(item.sales)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </ChartContainer>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ComparisonAnalytics; 